import { useEffect, useState } from "react";
import dayjs from "dayjs";
import { Area, AreaChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import {
  CalendarCheck, CreditCard, Eye, Globe, GraduationCap, Loader2, Monitor, MousePointerClick,
  Smartphone, Tablet, Users,
} from "lucide-react";
import { api } from "@/lib/api";

const RANGES = [7, 30, 90];

const pctChange = (cur, prev) => {
  if (!prev) return null;
  return Math.round(((cur - prev) / prev) * 100);
};

const Kpi = ({ testid, label, value, change, icon: Icon }) => (
  <div data-testid={testid} className="rounded-2xl border border-ink/10 bg-white p-5">
    <div className="flex items-center justify-between">
      <p className="font-outfit text-[11px] font-semibold uppercase tracking-widest text-faint">{label}</p>
      <Icon className="h-4 w-4 text-terra" />
    </div>
    <p className="mt-2 font-display text-3xl font-semibold text-ink">{(value ?? 0).toLocaleString("en-IN")}</p>
    {change !== null && change !== undefined && (
      <p className={`mt-1 font-outfit text-xs font-medium ${change >= 0 ? "text-sage" : "text-terra"}`}>
        {change >= 0 ? "↑" : "↓"} {Math.abs(change)}% vs previous period
      </p>
    )}
  </div>
);

const Card = ({ title, testid, children, className = "" }) => (
  <div data-testid={testid} className={`rounded-2xl border border-ink/10 bg-white p-5 ${className}`}>
    <p className="font-outfit text-[11px] font-semibold uppercase tracking-widest text-faint">{title}</p>
    <div className="mt-4">{children}</div>
  </div>
);

const BarRow = ({ label, value, max, sub }) => (
  <div className="flex items-center gap-3 py-1">
    <span className="w-44 shrink-0 truncate text-sm text-ink" title={label}>{label}</span>
    <div className="h-2 min-w-0 flex-1 overflow-hidden rounded-full bg-sand">
      <div className="h-full rounded-full bg-terra/80 transition-all duration-500"
           style={{ width: `${max ? Math.max(3, Math.round((value / max) * 100)) : 0}%` }} />
    </div>
    <span className="w-10 shrink-0 text-right font-outfit text-xs font-semibold text-plumsoft">{value}</span>
    {sub && <span className="hidden w-16 shrink-0 text-right text-[11px] text-faint sm:block">{sub}</span>}
  </div>
);

const Empty = ({ children }) => (
  <p className="py-4 text-center text-xs text-faint">{children || "No data yet for this period."}</p>
);

const DEVICE_ICON = { mobile: Smartphone, desktop: Monitor, tablet: Tablet };

export const AnalyticsTab = () => {
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let live = true;
    setLoading(true);
    api.get(`/admin/analytics?days=${days}`)
      .then((r) => live && setData(r.data))
      .catch(() => live && setData(null))
      .finally(() => live && setLoading(false));
    return () => { live = false; };
  }, [days]);

  if (loading && !data)
    return (
      <div className="flex items-center justify-center py-24" data-testid="analytics-loading">
        <Loader2 className="h-6 w-6 animate-spin text-terra" />
      </div>
    );
  if (!data)
    return <p className="py-24 text-center text-sm text-faint" data-testid="analytics-error">Could not load analytics.</p>;

  const k = data.kpis || {};
  const prev = k.prev || {};
  const funnelMax = Math.max(1, ...(data.funnel || []).map((f) => f.count));
  const pageMax = Math.max(1, ...(data.topPages || []).map((p) => p.views));
  const articleMax = Math.max(1, ...(data.topArticles || []).map((p) => p.views));
  const refMax = Math.max(1, ...(data.referrers || []).map((r) => r.views));
  const deviceMax = Math.max(1, ...(data.devices || []).map((d) => d.views));
  const browserMax = Math.max(1, ...(data.browsers || []).map((b) => b.views));
  const eventMax = Math.max(1, ...(data.events || []).map((e) => e.count));

  return (
    <div data-testid="analytics-tab" className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="font-display text-2xl font-semibold text-ink">Traffic & events</h2>
          <p className="mt-1 text-xs text-faint">First-party, privacy-friendly tracking — no cookies, no IP storage.</p>
        </div>
        <div className="flex gap-2" data-testid="analytics-range">
          {RANGES.map((d) => (
            <button
              key={d}
              data-testid={`analytics-range-${d}`}
              onClick={() => setDays(d)}
              className={`rounded-full px-4 py-2 font-outfit text-xs font-semibold transition-colors ${
                days === d ? "bg-ink text-sand" : "border border-ink/10 bg-white text-plumsoft hover:border-terra/40"
              }`}
            >
              {d} days
            </button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4 lg:grid-cols-3 xl:grid-cols-6" data-testid="analytics-kpis">
        <Kpi testid="kpi-visits" label="Visits" value={k.visits} change={pctChange(k.visits, prev.visits)} icon={Users} />
        <Kpi testid="kpi-visitors" label="Unique visitors" value={k.visitors} change={pctChange(k.visitors, prev.visitors)} icon={Globe} />
        <Kpi testid="kpi-pageviews" label="Pageviews" value={k.pageviews} change={pctChange(k.pageviews, prev.pageviews)} icon={Eye} />
        <Kpi testid="kpi-book-ctas" label="Book CTA clicks" value={k.bookCtas} change={pctChange(k.bookCtas, prev.bookCtas)} icon={MousePointerClick} />
        <Kpi testid="kpi-bookings" label="Bookings created" value={k.bookings} change={pctChange(k.bookings, prev.bookings)} icon={CalendarCheck} />
        <Kpi testid="kpi-enrolls" label="Enroll clicks" value={k.enrolls} change={pctChange(k.enrolls, prev.enrolls)} icon={GraduationCap} />
      </div>

      <Card title="Traffic over time" testid="analytics-chart-card">
        {data.daily?.some((d) => d.pageviews > 0) ? (
          <div className="h-64" data-testid="analytics-chart">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={data.daily} margin={{ top: 5, right: 8, left: -18, bottom: 0 }}>
                <defs>
                  <linearGradient id="gv" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#6B2737" stopOpacity={0.35} />
                    <stop offset="100%" stopColor="#6B2737" stopOpacity={0.02} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#2A121810" vertical={false} />
                <XAxis dataKey="day" tickFormatter={(d) => dayjs(d).format("D MMM")} tick={{ fontSize: 11, fill: "#6B4A55" }} tickLine={false} axisLine={false} />
                <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: "#6B4A55" }} tickLine={false} axisLine={false} />
                <Tooltip
                  labelFormatter={(d) => dayjs(d).format("ddd, D MMM YYYY")}
                  contentStyle={{ borderRadius: 12, border: "1px solid #2A121822", fontSize: 12 }}
                />
                <Area type="monotone" dataKey="pageviews" name="Pageviews" stroke="#6B2737" fill="url(#gv)" strokeWidth={2} />
                <Area type="monotone" dataKey="visitors" name="Unique visitors" stroke="#7A8F78" fill="none" strokeWidth={2} strokeDasharray="5 3" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        ) : (
          <Empty>No traffic recorded yet — visits will appear here as people browse the site.</Empty>
        )}
      </Card>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <Card title="Booking funnel" testid="analytics-funnel">
          {(data.funnel || []).some((f) => f.count > 0) ? (
            <div>
              {data.funnel.map((f, i) => (
                <div key={f.stage} className="flex items-center gap-3 py-1.5" data-testid={`funnel-stage-${i}`}>
                  <span className="w-40 shrink-0 text-sm text-ink">{f.stage}</span>
                  <div className="h-6 min-w-0 flex-1 overflow-hidden rounded-lg bg-sand">
                    <div className="flex h-full items-center rounded-lg bg-terra/85 pl-2 font-outfit text-[11px] font-semibold text-white transition-all duration-500"
                         style={{ width: `${Math.max(f.count ? 8 : 0, Math.round((f.count / funnelMax) * 100))}%` }}>
                      {f.count > 0 && f.count}
                    </div>
                  </div>
                  <span className="w-12 shrink-0 text-right text-[11px] text-faint">
                    {i > 0 && data.funnel[i - 1].count ? `${Math.round((f.count / data.funnel[i - 1].count) * 100)}%` : ""}
                  </span>
                </div>
              ))}
            </div>
          ) : (
            <Empty>No booking-funnel events yet.</Empty>
          )}
        </Card>

        <Card title="Top referrers" testid="analytics-referrers">
          {data.referrers?.length
            ? data.referrers.map((r) => <BarRow key={r.source} label={r.source} value={r.views} max={refMax} sub={`${r.visits} visits`} />)
            : <Empty />}
        </Card>

        <Card title="Top pages" testid="analytics-top-pages">
          {data.topPages?.length
            ? data.topPages.map((p) => <BarRow key={p.path} label={p.path} value={p.views} max={pageMax} sub={`${p.visitors} uniq`} />)
            : <Empty />}
        </Card>

        <Card title="Top articles" testid="analytics-top-articles">
          {data.topArticles?.length
            ? data.topArticles.map((p) => <BarRow key={p.path} label={p.path.replace("/blog/", "").replace(/\/$/, "").replace(/-/g, " ")} value={p.views} max={articleMax} sub={`${p.visitors} uniq`} />)
            : <Empty>No article reads yet.</Empty>}
        </Card>

        <Card title="Devices" testid="analytics-devices">
          {data.devices?.length
            ? data.devices.map((d) => {
                const Icon = DEVICE_ICON[d.name] || Monitor;
                return (
                  <div key={d.name} className="flex items-center gap-3 py-1">
                    <Icon className="h-4 w-4 shrink-0 text-terra" />
                    <div className="min-w-0 flex-1">
                      <BarRow label={d.name} value={d.views} max={deviceMax} />
                    </div>
                  </div>
                );
              })
            : <Empty />}
        </Card>

        <Card title="Browsers" testid="analytics-browsers">
          {data.browsers?.length
            ? data.browsers.map((b) => <BarRow key={b.name} label={b.name} value={b.views} max={browserMax} />)
            : <Empty />}
        </Card>

        <Card title="UTM campaigns" testid="analytics-utms">
          {data.utms?.length
            ? data.utms.map((u, i) => (
                <BarRow key={i} label={`${u.source}${u.medium ? " / " + u.medium : ""}${u.campaign ? " / " + u.campaign : ""}`} value={u.views} max={Math.max(...data.utms.map((x) => x.views))} />))
            : <Empty>No UTM-tagged visits yet. Add ?utm_source=instagram to links you share.</Empty>}
        </Card>

        <Card title="Events" testid="analytics-events">
          {data.events?.length
            ? data.events.map((e) => <BarRow key={e.name} label={e.name.replace(/_/g, " ")} value={e.count} max={eventMax} />)
            : <Empty />}
        </Card>
      </div>

      <Card title="Latest activity" testid="analytics-recent">
        {data.recent?.length ? (
          <div className="divide-y divide-ink/5">
            {data.recent.map((e, i) => (
              <div key={i} className="flex flex-wrap items-center gap-x-3 gap-y-0.5 py-2 text-xs" data-testid={`recent-event-${i}`}>
                <span className="w-32 shrink-0 font-outfit text-faint">{dayjs(e.ts).format("D MMM, h:mm A")}</span>
                <span className={`rounded-full px-2 py-0.5 font-outfit text-[10px] font-semibold uppercase tracking-wider ${
                  e.type === "event" ? "bg-terra/10 text-terra" : "bg-sage/15 text-sage"
                }`}>
                  {e.type === "event" ? (e.name || "event").replace(/_/g, " ") : "pageview"}
                </span>
                <span className="min-w-0 flex-1 truncate text-ink">{e.path}</span>
                <span className="text-faint">{e.device}</span>
                <span className="text-faint">{e.ref}</span>
              </div>
            ))}
          </div>
        ) : (
          <Empty />
        )}
      </Card>
    </div>
  );
};
